// Idioma de la interfaz → formatos de fecha, número y moneda (WS-2274).
//
// Único punto que traduce el idioma de ngx-translate («es», «ca», «en») a un
// locale. Úsalo en vez de escribir 'es-ES' a mano:
//   - TS:        `formatNumber(n, this.translate.currentLang)`,
//                en el dashboard de empresa `formatCount` (miles también con cuatro
//                cifras), `formatPercent` y `formatDateRange` (#20261102-155857),
//                `new Intl.DateTimeFormat(dateLocaleFor(this.translate.currentLang), …)`
//   - plantilla: `| date`, `| number`, `| currency` ya siguen el idioma (LOCALE_ID
//                se fija al arrancar, app.module.ts; cambiar de idioma recarga), o
//                `{{ fecha | langDate: 'dd MMM yyyy' }}` (shared/pipes/lang-date.pipe.ts).
// Los mensajes que se envían al usuario final (p. ej. payment-diagnosis) van
// en el idioma de ese usuario, no en el de la interfaz: no usan esto.

/** Idiomas de la interfaz. */
export type UiLang = 'es' | 'ca' | 'en';

/** Normaliza cualquier código («ca-ES», «EN», null…) a un idioma de la interfaz; por defecto «es». */
export function uiLang(lang: string | null | undefined): UiLang {
  const short = (lang ?? '').slice(0, 2).toLowerCase();
  return short === 'ca' || short === 'en' ? short : 'es';
}

/** Idioma de la interfaz → locale de `Intl` («es-ES», «ca-ES», «en-GB»). */
export function dateLocaleFor(lang: string | null | undefined): string {
  switch (uiLang(lang)) {
    case 'ca':
      return 'ca-ES';
    case 'en':
      return 'en-GB';
    default:
      return 'es-ES';
  }
}

/**
 * Idioma de la interfaz → locale registrado en Angular («es», «ca», «en-GB»;
 * ver `registerLocaleData` en app.module.ts) para `formatDate`/`DatePipe`.
 */
export function angularLocaleFor(lang: string | null | undefined): string {
  switch (uiLang(lang)) {
    case 'ca':
      return 'ca';
    case 'en':
      return 'en-GB';
    default:
      return 'es';
  }
}

/** Número con separadores del idioma: 1.234,5 / 1,234.5. */
export function formatNumber(
  value: number | null | undefined,
  lang: string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(dateLocaleFor(lang), options);
}

/**
 * Cifra del dashboard de empresa (#20261102-155857): como `formatNumber`, pero
 * con el separador de miles también con cuatro cifras («1.284» / «1,284»). En
 * es-ES, `Intl` no agrupa por defecto los números de cuatro cifras («1284»);
 * aquí se fuerza `useGrouping: 'always'` para que todas las páginas digan igual.
 */
export function formatCount(
  value: number | null | undefined,
  lang: string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  // `'always'` es de ES2023; el `lib` del proyecto (ES2021) solo tipa boolean.
  const grouping = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions;
  return new Intl.NumberFormat(dateLocaleFor(lang), { ...grouping, ...options }).format(Number(value));
}

/**
 * Porcentaje con el formato del idioma: `73` → «73 %» en es y ca (con el
 * espacio duro U+00A0 que da `Intl`, para que no se parta en dos líneas) y
 * «73%» en en. Recibe el porcentaje ya multiplicado (73, no 0,73). Sin
 * decimales salvo que se pidan; `signDisplay: 'exceptZero'` para variaciones.
 */
export function formatPercent(
  value: number | null | undefined,
  lang: string | null | undefined,
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat(dateLocaleFor(lang), {
    style: 'percent',
    maximumFractionDigits: 0,
    ...options,
  }).format(Number(value) / 100);
}

/** «AAAA-MM-DD» → fecha local (no UTC, que en Madrid puede caer el día anterior). */
function toLocalDate(value: Date | string): Date {
  if (value instanceof Date) return value;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Rango de fechas de los selectores de periodo (Uso, Viajes, Funnel, PMST):
 * con el mismo año, el año solo al final («4 oct – 2 nov 2026»); con años
 * distintos, en los dos extremos («4 dic 2025 – 2 ene 2026»). Lo resuelve
 * `Intl.DateTimeFormat.formatRange`, con las abreviaturas de cada idioma
 * («4 Oct – 2 Nov 2026», «4 d’oct. – 2 de nov. 2026»); dentro del mismo mes
 * junta los días («4–9 sept 2026»).
 */
export function formatDateRange(
  from: Date | string,
  to: Date | string,
  lang: string | null | undefined,
): string {
  const fmt = new Intl.DateTimeFormat(dateLocaleFor(lang), { day: 'numeric', month: 'short', year: 'numeric' });
  return fmt.formatRange(toLocalDate(from), toLocalDate(to));
}

/** Céntimos → «12,50 €» / «€12.50» según el idioma. */
export function formatEuroCentsFor(cents: number | null | undefined, lang: string | null | undefined): string {
  if (cents === null || cents === undefined || Number.isNaN(Number(cents))) return '—';
  return (Number(cents) / 100).toLocaleString(dateLocaleFor(lang), { style: 'currency', currency: 'EUR' });
}

/**
 * Idioma activo de la interfaz para utilidades puras (sin Angular) que no
 * reciben el idioma por parámetro, p. ej. `formatEuroCents` de
 * passenger-charge.ts. Lo mantiene al día TranslationService (onLangChange).
 */
let activeLang: UiLang = 'es';

export function setActiveLang(lang: string | null | undefined): void {
  activeLang = uiLang(lang);
}

/** Locale de `Intl` del idioma activo («es-ES», «ca-ES», «en-GB»). */
export function activeIntlLocale(): string {
  return dateLocaleFor(activeLang);
}
